/**
 * Plate geometry at the legal dimensions, in mm (1 SVG unit = 1 mm). Standard oblong plate
 * 520 × 111; 79 mm characters, 11 mm between characters, 33 mm between the two groups, an
 * 11 mm minimum margin all round, and an optional national-identifier band on the left no
 * wider than 50 mm (Display of Registration Marks Regulations 2001, Sch. 3–4; INF104).
 */
import { formatReg, regGroups } from './format';
import { GLYPH_HEIGHT, glyphFor } from './glyphs';

export const PLATE_WIDTH = 520;
export const PLATE_HEIGHT = 111;
export const CHAR_SPACING = 11;
export const GROUP_SPACING = 33;
export const PLATE_MARGIN = 11;
export const BAND_WIDTH = 50;
/** Corner radius of the plate itself (pressed acrylic plates are rounded, ~3% of the width). */
export const PLATE_RADIUS = 14;

export interface PlacedChar {
  ch: string;
  x: number;
  y: number;
  width: number;
}

export interface PlateLayout {
  /** The reg as shown: "AB12 CDE". */
  text: string;
  width: number;
  height: number;
  /** 0 when there is no identifier band. */
  bandWidth: number;
  /** Width of the characters plus their spacing. */
  textWidth: number;
  chars: PlacedChar[];
}

/** Width the characters of `groups` take on a plate, spacing included. */
export function textWidthOf(groups: string[]): number {
  let w = 0;
  groups.forEach((g, gi) => {
    if (gi > 0) w += GROUP_SPACING;
    [...g].forEach((ch, ci) => {
      if (ci > 0) w += CHAR_SPACING;
      w += glyphFor(ch)?.width ?? 0;
    });
  });
  return w;
}

/**
 * Lay out a reg on a standard plate. Characters are centred in the area right of the band;
 * a mark too long for 520 mm (only unknown, non-UK shapes) widens the plate instead of
 * squeezing the characters, so the type is always at the legal size.
 */
export function layoutPlate(reg: string, opts: { band?: boolean } = {}): PlateLayout {
  const groups = regGroups(reg)
    .map((g) => [...g].filter((ch) => glyphFor(ch)).join(''))
    .filter((g) => g.length > 0);
  const bandWidth = opts.band ? BAND_WIDTH : 0;
  const textWidth = textWidthOf(groups);
  const width = Math.max(PLATE_WIDTH, bandWidth + textWidth + 2 * PLATE_MARGIN);
  const y = (PLATE_HEIGHT - GLYPH_HEIGHT) / 2;
  let x = bandWidth + (width - bandWidth - textWidth) / 2;
  const chars: PlacedChar[] = [];
  groups.forEach((g, gi) => {
    if (gi > 0) x += GROUP_SPACING;
    [...g].forEach((ch, ci) => {
      if (ci > 0) x += CHAR_SPACING;
      const glyph = glyphFor(ch)!;
      chars.push({ ch, x, y, width: glyph.width });
      x += glyph.width;
    });
  });
  return { text: formatReg(reg), width, height: PLATE_HEIGHT, bandWidth, textWidth, chars };
}
